import React, { useCallback, useEffect, useState } from 'react';
import Step from '@mui/material/Step';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import StateMenu from '#component/shipment/form/delivery/menu';
import State from '#component/shipment/form/delivery/state';
import { Cleaner, isStateDone } from '#component/shipment/form/helper';
import { setDisplayFields as setDisplayFieldsAction, clearDisplayFields as clearDisplayFieldsAction } from '#action/shipment/delivery';
import StepIcon from '#component/icon/step-icon';
import {
  StyledStepper,
  StyledStepLabel,
  StyledStepContent,
  StyledStepConnector,
  StyledDivider,
  addNewStateButtonStyle,
  addNewItemButtonStyle
} from './style';
import AddressbookDialog from './addressbookDialog/addressbookDialog';
import TimelineSwapArrows from './timeline-swap-arrows';
import { uuidv4 } from '#helper';

const getStateLabel = (index, isLastIndex) =>
  (index === 0 ? 'ORIGIN' : isLastIndex ? 'DESTINATION' : 'CHECKPOINT');

const GetDivider = props => {
  const { addState, disabled } = props;
  return (
    <>
      <StateMenu
        style={addNewStateButtonStyle}
        addState={addState}
        disabled={disabled}
      />
      <StyledDivider />
    </>
  );
};

const RenderStates = props => {
  const {
    initialDisplayFields,
    disabled
  } = props;
  const methods = useFormContext();
  const { control, watch, getValues, formState } = methods || {};
  const { fields, append, insert, remove, update, swap } = useFieldArray({ control, name: 'states' });
  // `fields` entries are stale snapshots whose `id` is RHF's own key; read live values instead
  const getStateValues = index => getValues?.(`states.${index}`) ?? {};

  const displayFields = useSelector(s => s.delivery);
  const dispatch = useDispatch();
  const [init, setInit] = useState(false);

  const deliveryFormStates = watch?.('states') || [];
  const ORIGIN_ERRORS = ['Origin should have a date'];
  const statesErrors = formState?.errors?.states;
  const statesError = statesErrors?.root?.message ?? statesErrors?.message;
  const isOriginError = stepIndex => stepIndex === 0 && ORIGIN_ERRORS.includes(statesError);

  const setDisplayFields = useCallback(states =>
    dispatch(setDisplayFieldsAction(states)), [dispatch]);

  const fieldsWithDoneStatus = (fields || []).map((state, stateIndex) => {
    const currentState = deliveryFormStates[stateIndex] || state;
    if (isStateDone(currentState)) {
      return {
        ...currentState,
        done: currentState.date
      };
    }
    return currentState;
  });

  useEffect(() => {
    if (!init && initialDisplayFields.length !== 0) {
      setDisplayFields(initialDisplayFields);
      setInit(true);
    } else if (displayFields.length === 0) {
      const origin = {
        id: uuidv4(),
        date: new Date().toISOString()
      };
      append(origin);
      setDisplayFields([{ ...origin }]);
    }
  }, [init, displayFields.length, append, setDisplayFields, initialDisplayFields]);

  useEffect(() => () => dispatch(clearDisplayFieldsAction()), [dispatch]);

  const addState = index => state => {
    insert(index + 1, state);
    const newDisplayFields = [...displayFields];
    newDisplayFields.splice(index + 1, 0, state);
    setDisplayFields(newDisplayFields);
  };

  const updateState = index => state => {
    const newState = { ...state };
    if (index === 0 && !Number.isInteger(newState.geofenceDirection)) {
      newState.geofenceDirection = 0;
    }
    update(index, { ...newState, ...Cleaner.cleanObject(getStateValues(index)) });
    const newDisplayFields = [...displayFields];
    newDisplayFields.splice(index, 1, newState);
    setDisplayFields(newDisplayFields);
  };

  const deleteAttrFromState = index => attr => {
    const newState = { ...getStateValues(index) };
    Object.keys(newState).forEach(a => {
      if (attr.includes(a)) {
        delete newState[a];
      }
    });
    const deleteState = Object.keys(newState).reduce((acc, item) => {
      if (item !== 'id' && ['date', 'company', 'formattedAddress', 'address', 'locations'].includes(item)) {
        return false;
      }
      return acc;
    }, true);
    const newDisplayFields = [...displayFields];

    if (deleteState) {
      remove(index);
      newDisplayFields.splice(index, 1);
      setDisplayFields(newDisplayFields);
    } else {
      update(index, newState);
      newDisplayFields.splice(index, 1, newState);
      setDisplayFields(newDisplayFields);
    }
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [contactState, setContactState] = useState('');
  const [formIndex, setFormIndex] = useState(null);
  const [pristineDeliveryForm, setPristineDeliveryForm] = useState(true);

  const handleOpenAddressbookDialog = useCallback((stepIndex, isPrestineDeliveryForm) => {
    const formValues = deliveryFormStates[stepIndex];
    setContactState(formValues);
    setFormIndex(stepIndex);
    setPristineDeliveryForm(isPrestineDeliveryForm);
    setDialogOpen(true);
  }, [deliveryFormStates]);

  return (
    <>
      <StyledStepper
        orientation="vertical"
        connector={<StyledStepConnector />}
      >
        {fieldsWithDoneStatus.length > 0 && fieldsWithDoneStatus.map((state, index, self) => (
          <Step
            key={state.id || index}
            active={!!state.done}
            style={isOriginError(index) ? { outline: '1px solid red', outlineOffset: 24 } : undefined}
            expanded={true}
          >
            <StyledStepLabel
              StepIconComponent={StepIcon}
              optional={<TimelineSwapArrows
                currentIndex={index}
                lastIndex={self.length - 1}
                disabled={disabled}
                fields={{ swap }}
                displayFields={displayFields}
                setDisplayFields={setDisplayFields}
              />}
            >
              {getStateLabel(index, index === self.length - 1)}
              <button type="submit" style={{ position: 'absolute', visibility: 'hidden' }}>+</button>
              <StateMenu
                index={index}
                style={addNewItemButtonStyle}
                state={displayFields[index]}
                addState={updateState(index)}
                deleteAttrFromState={deleteAttrFromState(index)}
                disabled={disabled}
              />
            </StyledStepLabel>
            <StyledStepContent>
              <State
                index={index}
                member={`states[${index}]`}
                state={displayFields[index]}
                disabled={disabled}
                handleOpenAddressbookDialog={handleOpenAddressbookDialog}
              />
              <GetDivider
                disabled={disabled}
                addState={addState(index)}
              />
            </StyledStepContent>
          </Step>
        ))}
      </StyledStepper>
      <AddressbookDialog
        isOpen={dialogOpen}
        isDeliveryFormPrestine={pristineDeliveryForm}
        setDialogOpen={setDialogOpen}
        formValues={contactState}
        formIndex={formIndex}
        deliveryFormStates={deliveryFormStates}
      />
    </>
  );
};

const Timeline = ({ states, disabled }) => (
  <RenderStates
    initialDisplayFields={states || []}
    disabled={disabled}
  />
);

export default Timeline;
